import {FileUpload, Loader} from '@/components/atoms'
import {sanitizeFileName} from '@/helpers/shadcn/utils'
import {useAzureBlob} from '@/hooks/useAzureBlob-hook'
import {useCSVFileValidation} from '@/hooks/useCSVFileValidation-hook'
import {useFileUpload} from '@/hooks/useFileUpload'
import {INVENTORY_UPLOAD_HISTORY_KEY} from '@/hooks/useUploadHistory'
import {addMetaData, publishProductList} from '@/services/metadata'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {Constants, NUMBERS} from '@/utils/constants/commonConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {useQueryClient} from '@tanstack/react-query'
import Papa from 'papaparse'
import {forwardRef, useImperativeHandle, useState} from 'react'

const UploadFile = forwardRef(
  (
    {
      acceptedExtensions,
      description,
      label,
      files,
      multiple = false,
      className = '',
      containerClassName = '',
      required = false,
      variant,
      buttonLabel = Constants.UploadFile,
      type,
      imageUri = '',
      setFile = () => {},
      uploadFileSuccess,
      resetUploadFile,
      isDark = true,
      onUpload = () => {},
      feedbackType,
      onUploadDelete = () => {},
      helperText = '',
      onUploadError = () => {},
      fileSizeCheck = false,
      dataTestId = '',
      disabled = false,
      uploadFor,
      onSetError = () => {},
      isCsvValidationRequired = false,
      isTemplateUpload = false,
      setCsvErrors = () => {},
      isCloseModal = () => {},
      setHelperText = () => {},
      toastMessage = 'File has successfully been uploaded and the status can be checked in Upload History',
    },
    ref
  ) => {
    const {uploadFile} = useFileUpload()
    const {validateCSVFile} = useCSVFileValidation()
    const {uploadFileOnBlob} = useAzureBlob()
    const [error, setError] = useState(false)
    const [fileError, setFileError] = useState({})
    const [uploading, setUploading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const dispatch = useAppDispatch()
    const [uploadedFile, setUploadedFile] = useState({})
    const [isDwnldBtnClicked, setDwnldBtnCkicked] = useState(false)
    const inventoryCsvRules = useFeatureValue(growthBookFeatureFlags.InventoryCsvValidationRules)
    const productsCsvRules = useFeatureValue(growthBookFeatureFlags.productsCsvValidationRules)
    const queryClient = useQueryClient()
    const getBase64 = file => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        dispatch(
          uploadFileSuccess({
            title: file?.name,
            imageBase64: reader.result,
            imageUri: URL.createObjectURL(file),
          })
        )
      }
    }

    const productsCsvRule = {
      columns: [
        {name: 'Product Id', required: true, type: 'string', minChar: 0, maxChar: 255},
        {name: 'Product Name', required: true, type: 'string', minChar: 0, maxChar: 255},
        {name: 'Is_Returnable', required: false, type: 'string', minChar: 0, maxChar: 255},
        {name: 'Show on E-Commerce', required: false, type: 'boolean'},
        {name: 'Show on Engagement', required: false, type: 'boolean'},
        {name: 'Sorting', required: false, type: 'integer'},
      ],
      type: 'text/csv',
      size: '5120',
    }

    const csvValidation = file => {
      const validationRules = uploadFor === 'inventory' ? inventoryCsvRules : productsCsvRules || productsCsvRule
      Papa.parse(file, {
        skipEmptyLines: true,
        complete: async ({data}) => {
          const validationErrors = validateCSVFile(
            validationRules,
            {
              fileType: file?.type,
              fileSize: file?.size / 1024,
              csvData: data,
            },
            uploadFor
          )
          setTotalCount(data?.length)
          if (validationErrors.length > 0) {
            setCsvErrors(validationErrors)
            setError(true)
            onUploadError()
            setFileError({feedbackType: Constants.Error})
          } else {
            try {
              setUploadedFile(file)
              onUpload(file)
              setUploading(false)
              onSetError(false)
            } catch (err) {
              console.error('Upload failed:', err)
            }
          }
        },
        header: true,
      })
    }
    const uploadBulkPromotionFile = async file => {
      setDwnldBtnCkicked(true)
      if (!file) return
      const res = await uploadFile(file, Constants.bulkUploadPromotions, true)
      if (res?.status?.toLowerCase() === 'success') {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'File has been uploaded successfully.',
            type: 'success',
          })
        )
        isCloseModal?.()
      } else {
        setFileError({feedbackType: Constants.Error, helperText: res?.message || Constants.ErrorForFileUpload})
      }
      setDwnldBtnCkicked(false)
    }
    useImperativeHandle(ref, () => ({
      fileUpload: () => {
        getFileUploadDetails(uploadedFile)
      },
      bulkPromotionFileUpload: () => {
        uploadBulkPromotionFile(uploadedFile)
      },
    }))
    const handleDelete = () => {
      setError(false)
      setFileError({})
      resetUploadFile && dispatch(resetUploadFile())
      setFile([])
      onUploadDelete()
    }
    const extractUUID = str => {
      const match = str.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/)
      return match ? match[0] : null
    }
    const getFileUploadDetails = async file => {
      if (isDwnldBtnClicked) return

      setDwnldBtnCkicked(true)
      setUploading(true)
      try {
        const uploadDate = new Date(Date.now() - 300000) // Sometimes uploadDate is after timestamp so we get an error so we subtract 5 minutes
        const sanitizedFileName = sanitizeFileName(file.name)
        const sanitizedFile = new File([file], sanitizedFileName, {type: file.type})

        const res = await uploadFile(sanitizedFile, uploadFor, isTemplateUpload)

        const isError = !res || res.success === false || Object.keys(res).length === NUMBERS.Zero
        if (isError) {
          setFileError({
            feedbackType: Constants.Error,
            helperText: res?.message || Constants.ErrorForFileUpload,
          })
          onSetError(true)
          return
        }

        // For product and inventory template uploads, use new response structure (fileUploadUUID)
        // if (isTemplateUpload && (uploadFor === 'product' || uploadFor === 'inventory')) {
        //   if (!res?.fileUploadUUID) {
        //     setFileError({feedbackType: Constants.Error, helperText: 'Unexpected response from server.'})
        //     onSetError(true)
        //     return
        //   }

        //   // Upload successful, continue
        //   onUpload(file)
        //   onSetError(false)

        //   const uuid = res.fileUploadUUID
        //   const metadataResponse = await addMetaData({
        //     fileUploadUUID: uuid,
        //     applicationId: 1,
        //     fileName: res?.name,
        //     fileSize: file.size,
        //     expectedCount: totalCount,
        //     uploadDate,
        //     storageContainer: 'input',
        //     storagePath: res.directory || '/',
        //     createdBy: 'admin',
        //     status: 'DRAFT',
        //     fileType: 'csv',
        //     templateType: uploadFor,
        //     countryCode: 'MX',
        //     scope: 'pepsi',
        //   })

        //   if (metadataResponse?.isSuccess && metadataResponse?.statusCode == 200) {
        //     await publishProductList?.({
        //       uuid,
        //       status: 'published',
        //       updatedBy: 'user',
        //       goalConfigId: 1,
        //     })

        //     dispatch(
        //       setToastNotifications({
        //         show: true,
        //         message: toastMessage,
        //         type: 'success',
        //       })
        //     )

        //     isCloseModal?.()
        //     queryClient.invalidateQueries({queryKey: [INVENTORY_UPLOAD_HISTORY_KEY]})
        //     return
        //   }
        //   setFileError({feedbackType: Constants.Error, helperText: 'Unexpected response from server.'})
        //   onSetError(true)
        //   return
        // }

        // For other upload types, check for old response structure (blobUrl)
        if (!res?.blobUrl) {
          setFileError({feedbackType: Constants.Error, helperText: 'Unexpected response from server.'})
          onSetError(true)
          return
        }

        // Upload successful, continue
        onUpload(file)
        onSetError(false)

        // Handle old response structure (blobUrl) - only for non-product/inventory template uploads
        const renamedFile = new File([file], res.fileName, {type: file.type})
        const blobResponse = await uploadFileOnBlob(res, renamedFile)
        if (blobResponse !== true) {
          setFileError({feedbackType: Constants.Error, helperText: 'Blob upload failed.'})
          onSetError(true)
          return
        }

        const uuid = extractUUID(res.fileName)
        const metadataResponse = await addMetaData({
          fileUploadUUID: uuid,
          applicationId: 1,
          fileName: res?.fileName,
          fileSize: file.size,
          expectedCount: totalCount,
          uploadDate,
          storageContainer: 'input',
          storagePath: res.directory,
          createdBy: 'admin',
          status: 'DRAFT',
          fileType: 'csv',
          templateType: uploadFor,
          countryCode: 'MX',
          scope: 'pepsi',
        })

        if (metadataResponse?.isSuccess && metadataResponse?.statusCode == 200) {
          await publishProductList?.({
            uuid,
            status: 'published',
            updatedBy: 'user',
            goalConfigId: 1,
          })

          dispatch(
            setToastNotifications({
              show: true,
              message: toastMessage,
              type: 'success',
            })
          )

          isCloseModal?.()
          queryClient.invalidateQueries({queryKey: [INVENTORY_UPLOAD_HISTORY_KEY]})
          return
        }
        setFileError({feedbackType: Constants.Error, helperText: 'Unexpected response from server.'})
        onSetError(true)
      } catch (err) {
        setFileError({
          feedbackType: Constants.Error,
          helperText: `Error verifying columns: ${err.message}`,
        })
        onSetError(true)
      } finally {
        setUploading(false)
        setDwnldBtnCkicked(false)
      }
    }

    const fileUpload = async file => {
      try {
        const res = await uploadFile(file, uploadFor, isTemplateUpload)

        if (!res || res.success === false || Object.keys(res).length === NUMBERS.Zero) {
          setFileError({feedbackType: Constants.Error, helperText: res?.message || Constants.ErrorForFileUpload})
          setUploading(false)
          onSetError(true)
          return
        }

        if (res?.message === Constants.Success) {
          uploadFileSuccess && getBase64(file)
          onUpload(file)
          setUploading(false)
          onSetError(false)
        }
      } catch (err) {
        setFileError({feedbackType: Constants.Error, helperText: `Error verifying columns: ${err.message}`})
        setUploading(false)
        onSetError(true)
      }
    }

    const handleUpload = async file => {
      setError(false)
      setFileError({})
      onSetError(false)
      setUploading(true)

      if (file?.type === 'text/csv' && isCsvValidationRequired) return csvValidation(file)

      if (fileSizeCheck) {
        const fileSizeMB = file.size / (1024 * 1024)
        if (fileSizeMB > NUMBERS.Fifty) {
          setFileError({feedbackType: 'error', helperText: 'File cannot be uploaded. File size should be below 50MB'})
          return
        }
      }

      setFile([file])
      await fileUpload(file)
    }

    const getFeedbackType = () => (Object.keys(fileError).length > 0 ? fileError.feedbackType : feedbackType)
    const isFileError = !!Object.keys(fileError).length
    const getHelperText = () => {
      const text = isFileError || error ? fileError.helperText || helperText : helperText
      if (Array.isArray(text)) {
        return (
          <div>
            {text.map((err, index) => (
              <p key={index}>{err}</p>
            ))}
          </div>
        )
      }
      return text
    }
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes

    const handleXslxUpload = async file => {
      setError(false)
      setFileError({})
      onSetError(false)
      setUploading(true)
      setHelperText('')
      if (file?.size > MAX_FILE_SIZE) {
        setError(true)
        setFileError({
          feedbackType: Constants.Error,
          helperText: 'File size should be below 10MB',
        })
        setFile([file])
        setUploading(false)
        return
      }
      setFile([file])
      setUploading(false)
      onUpload(file)
      setUploadedFile(file)
    }

    return (
      <>
        {isDwnldBtnClicked && <Loader />}
        <FileUpload
          acceptedExtensions={acceptedExtensions}
          description={description}
          label={label}
          variant={uploading ? Constants.Uploading : variant}
          buttonLabel={buttonLabel}
          onUpload={acceptedExtensions === Constants.Xlsx ? handleXslxUpload : handleUpload}
          onUploadDelete={handleDelete}
          type={type}
          required={required}
          imageUri={imageUri}
          containerClassName={`${!isFileError && containerClassName} ${error || isFileError ? '!border-2 !border-dotted !border-red-500' : ''}`}
          className={className}
          multiple={multiple}
          value={isFileError ? '' : files}
          isDark={isDark}
          feedbackType={getFeedbackType()}
          helperText={getHelperText()}
          onUploadError={() => {
            setError(true)
            setFileError({feedbackType: Constants.Error})
            setHelperText(`File format is not valid. Supported formats: ${acceptedExtensions}`)
          }}
          dataTestId={dataTestId}
          disabled={disabled || isDwnldBtnClicked}
        />
      </>
    )
  }
)
export default UploadFile
